/**
 * Expense Management Utilities
 * Handles dynamic server hostname resolution for document attachments.
 */

/**
 * Dynamically constructs the absolute URL for retrieving an attachment by UUID.
 * Leverages the connected API client base hostname so that changes in the backend
 * URL do not require database updates.
 *
 * @param {string} uuid - The unique GCS media file record UUID
 * @returns {string} The fully qualified absolute download URL
 */
export const getAttachmentUrl = (uuid) => {
  if (!uuid) return "";
  const graphqlUri = process.env.REACT_APP_API_URL || "http://localhost:3008/graphql";
  const apiBase = graphqlUri.replace("/graphql", "");
  return `${apiBase}/getFileData?uuid=${uuid}`;
};

/**
 * Parses and normalizes raw expense attachments JSON/string arrays retrieved from the DB.
 * Guarantees every element contains a valid, dynamically reconstructed `.url` property.
 *
 * @param {string|Array} attachmentsField - Raw attachments data from the database
 * @returns {Array} List of normalized attachment objects with live absolute URLs
 */
export const normalizeAttachments = (attachmentsField) => {
  if (!attachmentsField) return [];
  try {
    const list = typeof attachmentsField === 'string' ? JSON.parse(attachmentsField) : attachmentsField;
    if (!Array.isArray(list)) return [];
    
    return list.map(item => ({
      name: item.name || "attachment",
      uuid: item.uuid || "",
      // Prioritize dynamic resolution if a UUID is present; safely fallback to legacy hardcoded URLs
      url: item.uuid ? getAttachmentUrl(item.uuid) : (item.url || "")
    }));
  } catch (e) {
    console.error("Error parsing attachments metadata:", e);
    return [];
  }
};
